/**
 * Normalized survey scoring engine.
 *
 * Convention:
 *   - Raw response is 1..5 (Strongly Disagree → Strongly Agree).
 *   - A question marked reverse:true means "high raw = WORSE outcome"
 *     (negatively-worded statements like "I feel overwhelmed").
 *     We flip it: 5→0, 4→25, 3→50, 2→75, 1→100.
 *   - A question marked reverse:false means "high raw = BETTER outcome"
 *     (positively-worded statements like "I feel resilient").
 *     Linear: 1→0, 2→25, 3→50, 4→75, 5→100.
 *
 * Categories: "mental" | "physical" | "emotional"
 *   Each contributes to its own 0-100 average.
 *
 * Back Pain Transformation Index (BPTI) is the mean of all three categories.
 * Range: 0 (worst) → 100 (best).
 */

const SCALE_MAX = 5;
const SCALE_MIN = 1;

export function normalizeAnswer(rawValue, reverse) {
  const v = Number(rawValue);
  if (!Number.isFinite(v) || v < SCALE_MIN || v > SCALE_MAX) return null;
  const pct = ((v - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100; // 0..100 forward
  return reverse ? 100 - pct : pct;
}

/**
 * Score one survey (a single week's questionnaire).
 *
 * @param {Array} questions  - question definitions with id/reverse/category
 * @param {Object} answers   - map of question id → raw 1..5 value
 * @returns {{
 *   raw_total: number,       // sum of raw 1..5 answers (max = 5 * questionsCount)
 *   raw_avg: number,         // raw_total / answered count
 *   normalized: number,      // 0..100 average across all questions
 *   categories: { mental: number|null, physical: number|null, emotional: number|null },
 *   answered: number,
 *   total: number,
 * }}
 */
export function scoreSurvey(questions, answers) {
  if (!Array.isArray(questions) || !answers) {
    return { raw_total: 0, raw_avg: 0, normalized: 0, categories: { mental: null, physical: null, emotional: null }, answered: 0, total: 0 };
  }

  const buckets = { mental: [], physical: [], emotional: [] };
  const allNormalized = [];
  let rawTotal = 0;
  let answered = 0;

  for (const q of questions) {
    const raw = answers[q.id];
    if (raw == null) continue;
    answered += 1;
    rawTotal += Number(raw) || 0;

    const norm = normalizeAnswer(raw, !!q.reverse);
    if (norm == null) continue;
    allNormalized.push(norm);

    const cat = q.category;
    if (cat && buckets[cat]) buckets[cat].push(norm);
  }

  const avg = (arr) => (arr.length === 0 ? null : Math.round(arr.reduce((a, b) => a + b, 0) / arr.length));

  return {
    raw_total: rawTotal,
    raw_avg: answered ? +(rawTotal / answered).toFixed(2) : 0,
    normalized: allNormalized.length ? Math.round(allNormalized.reduce((a, b) => a + b, 0) / allNormalized.length) : 0,
    categories: {
      mental: avg(buckets.mental),
      physical: avg(buckets.physical),
      emotional: avg(buckets.emotional),
    },
    answered,
    total: questions.length,
  };
}

/**
 * Aggregate normalized scores across one or many surveys into a single BPTI.
 *
 * If a category has no answers in any survey, it's reported as null and
 * excluded from BPTI computation.
 */
export function aggregateBPTI(allQuestions, allAnswers) {
  const buckets = { mental: [], physical: [], emotional: [] };

  for (const q of allQuestions) {
    const raw = allAnswers[q.id];
    if (raw == null) continue;
    const norm = normalizeAnswer(raw, !!q.reverse);
    if (norm == null) continue;
    if (q.category && buckets[q.category]) buckets[q.category].push(norm);
  }

  const avg = (arr) => (arr.length === 0 ? null : Math.round(arr.reduce((a, b) => a + b, 0) / arr.length));
  const mental = avg(buckets.mental);
  const physical = avg(buckets.physical);
  const emotional = avg(buckets.emotional);

  const present = [mental, physical, emotional].filter((v) => v != null);
  const bpti = present.length ? Math.round(present.reduce((a, b) => a + b, 0) / present.length) : null;

  return { mental, physical, emotional, bpti };
}

/** Human-readable tier label for a 0-100 score. */
export function tierLabel(score) {
  if (score == null) return "—";
  if (score >= 80) return "Thriving";
  if (score >= 60) return "Strong";
  if (score >= 40) return "Mixed";
  if (score >= 20) return "Struggling";
  return "Survival";
}

export const CATEGORY_META = {
  mental: { label: "Mental", color: "#22D3EE", desc: "Fear, confidence, beliefs, self-talk" },
  physical: { label: "Physical", color: "#F59E0B", desc: "Pain, mobility, energy, posture" },
  emotional: { label: "Emotional", color: "#10B981", desc: "Stress, resilience, hope, suppression" },
};
